import { useEffect } from 'react';

import { METADATA } from '@flexo/reducers';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

function getEndUserMemberSites(member: any, entities: any) {
  const allSites = entities?.site || entities?.sites || [];
  const memberSiteIds = new Set(
    [
      ...(((member?.sites as any[]) || []).map((site: any) => site?.siteId)),
      ...(((member?.changelog as any[]) || []).map(
        (entry: any) => entry?.siteId
      )),
    ].filter(Boolean)
  );
  const memberSites = [
    ...allSites.filter((site: any) =>
      memberSiteIds.size > 0
        ? memberSiteIds.has(site?.siteId)
        : site?.memberId === member?.memberId
    ),
    ...(((member?.sites as any[]) || []).filter((site: any) => site?.siteId)),
  ];

  return Array.from(
    new Map(
      memberSites
        .filter((site: any) => site?.siteId)
        .map((site: any) => [site.siteId, site])
    ).values()
  );
}

/**
 * FLEXO-6068 / end-user web: ad-hoc replacement for `MemberResolver`.
 *
 * The admin `MemberResolver` calls `GET /community/catalog/v1_3/member/{id}`,
 * which is admin-only and returns 403 (ERR_AUTH_003) for the `org:end_user`
 * role — even for the user's own member id. The mobile app never calls that
 * endpoint; it works off the already-authorized `entities` response.
 *
 * This resolver mirrors `SiteOverviewResolver`: it reads the member from the
 * `entities` already loaded by `EntitiesResolver` (which IS authorized for
 * end users) and sets it as the selected entity — no extra HTTP call, no 403.
 *
 * FLEXO-6062 routing: from the base `/member/:id` landing it also redirects a
 * single-site member to `/site/:siteId` (their one site). Multi-site members
 * stay on the `/member` aggregate dashboard.
 */
export const EndUserMemberResolver = () => {
  const dispatch = useDispatch();
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const pathSegment = location.pathname.split('/')[1];
  const metadata = useSelector((state: any) => state.metadata);

  function endUserMemberInit() {
    const entities = metadata?.entities as any;
    const members = [
      ...((entities?.member || entities?.members || []) as any[]),
      ...((metadata?.member?.current || []) as any[]),
      ...((metadata?.member?.previous || []) as any[]),
      ...((metadata?.member?.future || []) as any[]),
    ].reduce((acc: any[], member: any) => {
      if (
        member?.memberId &&
        !acc.some((item: any) => item?.memberId === member.memberId)
      ) {
        acc.push(member);
      }
      return acc;
    }, []);

    let _member: any = null;
    if (!params.id && members.length > 0) {
      _member = members[0];
    } else if (params.id) {
      _member = members.find((m: any) => m.memberId === params.id);
    }

    if (!_member) {
      return;
    }

    dispatch(
      METADATA.SetSelectedEntity({
        selectedEntity: _member,
        id: _member?.memberId,
        entityType: 'member',
      } as any)
    );

    // FLEXO-6062: single-site end users land on /site/:siteId; multi-site end
    // users stay on the /member aggregate. Only redirect from the base `member`
    // landing route (not member-energy / member-economics).
    if (pathSegment === 'member') {
      // A member's sites may come from `member.sites` or from changelog entries.
      // The site's own `memberId` is only a fallback because shared sites can
      // point to a different primary member.
      const memberSites = getEndUserMemberSites(_member, entities);
      if (memberSites.length === 1) {
        navigate(`/site/${memberSites[0].siteId}${location.search}`, {
          replace: true,
        });
      }
    }
  }

  useEffect(() => {
    endUserMemberInit();
  }, [params.id, metadata?.entities, metadata?.member]);

  return <></>;
};

export default EndUserMemberResolver;
